import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/KpiCard";
import { DocumentDialog } from "@/components/documents/DocumentDialog";
import { TypesDialog } from "@/components/documents/TypesDialog";
import { DocFiles } from "@/components/documents/DocFiles";
import { DriveConnect } from "@/components/damages/DriveConnect";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, stationColor } from "@/lib/format";
import { sortRows } from "@/lib/sort";
import { SortSelect } from "@/components/SortSelect";

const SORTS = [["created_at:desc", "Caricati di recente"], ["created_at:asc", "Caricati meno di recente"], ["doc_date:desc", "Data documento (recenti)"], ["doc_date:asc", "Data documento (vecchi)"], ["type_name:asc", "Tipologia (A-Z)"], ["title:asc", "Titolo (A-Z)"], ["plate:asc", "Targa (A-Z)"], ["station_name:asc", "Stazione (A-Z)"]];

export default function DocumentsPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showTypes, setShowTypes] = useState(false);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [order, setOrder] = useState("created_at:desc");

  const sid = isAdmin && stationId !== "all" ? stationId : undefined;
  const load = useCallback(() => { api.get("/documents", { params: { station_id: sid } }).then((r) => setDocs(r.data)); }, [sid]);
  const loadTypes = () => api.get("/documents/types").then((r) => setTypes(r.data));
  useEffect(load, [load]);
  useEffect(() => {
    api.get("/stations").then((r) => setStations(r.data));
    api.get("/fleet/vehicles").then((r) => setVehicles(r.data.vehicles));
    loadTypes();
  }, []);

  const items = useMemo(() => {
    const t = q.trim().toLowerCase();
    return docs.filter((d) => (typeFilter === "all" || d.type_id === typeFilter)
      && (!t || [d.type_name, d.title, d.notes, d.plate, d.station_name, d.created_by_name, ...d.files.map((x) => x.filename)].some((x) => (x || "").toLowerCase().includes(t))));
  }, [docs, q, typeFilter]);
  const sorted = useMemo(() => { const [k, d] = order.split(":"); return sortRows(items, k, d); }, [items, order]);

  const remove = async (d) => {
    if (!window.confirm("Eliminare questo documento e i suoi allegati?")) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Documento eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportXlsx = async () => {
    try {
      const { data: blob, headers } = await api.get("/documents/export", { params: { station_id: sid }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "documenti.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const title = isAdmin ? (stationId === "all" ? "Documenti" : `Documenti ${stations.find((x) => x.id === stationId)?.name || ""}`) : `Documenti ${stations[0]?.name || ""}`;

  return (
    <div className="fade-up">
      <PageHeader title={title} subtitle="Archivio documenti con allegati PDF o foto.">
        {isAdmin && <Button variant="outline" onClick={() => setShowTypes(true)} data-testid="document-types-button"><Settings2 className="mr-2 h-4 w-4" /> Tipologie</Button>}
        <Button variant="outline" onClick={exportXlsx} data-testid="document-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button onClick={() => setEditing({ station_id: sid || "" })} data-testid="document-add-button"><Plus className="mr-2 h-4 w-4" /> Nuovo documento</Button>
      </PageHeader>

      {isAdmin && <DriveConnect />}
      {isAdmin && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="document-station-tabs">
          {[{ id: "all", name: "Tutte le stazioni" }, ...stations].map((st) => (
            <button key={st.id} onClick={() => setStationId(st.id)} data-testid={`document-tab-${st.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === st.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {st.id !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: stationColor(stations, st.id) }} />}{st.name}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-64">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="document-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le tipologie</SelectItem>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca tipologia, titolo, targa, note..." className="pl-9" data-testid="document-search-input" />
        </div>
        <SortSelect value={order} onChange={setOrder} options={SORTS} testId="document-sort-select" />
        <span className="text-sm text-slate-500" data-testid="document-count">{items.length} documenti</span>
      </div>

      <div className="grid gap-3 lg:grid-cols-2" data-testid="document-list">
        {sorted.map((d) => (
          <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`document-row-${d.id}`}>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs">{d.type_name}</span>{d.title}
                </p>
                <p className="mt-1 text-xs text-slate-500">{d.station_name}{d.doc_date && ` · del ${fmtDate(d.doc_date)}`}{d.plate && <> · Targa <b className="font-mono-num">{d.plate}</b></>} · caricato il {new Date(d.created_at).toLocaleDateString("it-IT")} da {d.created_by_name}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEditing(d)} data-testid={`document-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(d)} data-testid={`document-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            {d.notes && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{d.notes}</p>}
            <div className="mt-3"><DocFiles files={d.files} testId={`document-files-${d.id}`} /></div>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500 lg:col-span-2" data-testid="document-empty">Nessun documento.</p>}
      </div>
      <DocumentDialog item={editing} types={types} stations={stations} vehicles={vehicles} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <TypesDialog open={showTypes} types={types} onClose={() => setShowTypes(false)} onChanged={loadTypes} />
    </div>
  );
}
